import React, {useContext, useEffect, useReducer, useState} from 'react';
import {
	Alert,
	AlertTitle,
	Avatar,
	Backdrop,
	Button,
	CircularProgress,
	Paper,
	Skeleton,
	Typography
} from "@mui/material";
import ArticleTwoToneIcon from "@mui/icons-material/ArticleTwoTone";
import AssignmentTwoToneIcon from "@mui/icons-material/AssignmentTwoTone";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import PersonOffTwoToneIcon from "@mui/icons-material/PersonOffTwoTone";
import PersonTwoToneIcon from "@mui/icons-material/PersonTwoTone";
import PhoneIphoneTwoToneIcon from "@mui/icons-material/PhoneIphoneTwoTone";
import clsx from "clsx";
import {useHistory, useParams} from "react-router-dom";
import StoricoCard from "../storico/StoricoCard";
import {getTicketHistory, searchTicket} from "../../../../services/TicketServices";
import {AnagraficaContext, AppContext, TicketContext} from "../../../../App";
import {getAnagraficaById, getPazientiAssociati} from "../../../../services/AnagraficaServices";
import {AUTOMATION_INITIAL_STATE, AutomationReducer, initAutomationState} from "../../../../reducers/AutomationReducer";
import {onClickDetails} from "../../../../services/AutomationServices";
import {getAccountInfo} from "../../../../utils/GenericUtils";
import DynamicForm from "./DynamicForm";
import AppPage from "../../../widgets/layout/AppPage";
import {TicketStyle} from "../../../../styles/pages/tickets/TicketStyle";
import TicketSummaryManager from "./TicketSummaryManager";
import TicketHistoryComposer from "../storico/TicketHistoryComposer";
import SimpleDialog from "../../../widgets/dialogs/SimpleDialog";

const cleanTicketText = (value) => {
	const text = value || "";
	return text.replace(/#\S*/g, "").replace(/\s+/g, " ").trim();
};

const firstValue = (...values) => values.find((value) => value !== null && value !== undefined && String(value).trim() !== "") || "";

const getAssigneeLabel = (ticket, automationState) => (
	automationState.operatorOnClick?.replace("ERR_", "")
	|| ticket?.assignedTo
	|| ticket?.assegnatario
	|| ticket?.lastModify
	|| "Non indicato"
);

const getCustomerData = (anagrafica, ticket) => {
	const subject = ticket?.soggetto || {};
	const source = anagrafica?.idSoggetto ? anagrafica : subject;

	return {
		nome: firstValue(source.nome, subject.nome),
		cognome: firstValue(source.cognome, subject.cognome),
		cell: firstValue(source.cell, source.cellulare, source.telefono, subject.cell, ticket?.cell),
		email: firstValue(source.email, subject.email, ticket?.email),
		codF: firstValue(source.codF, subject.codF),
	};
};

const TicketDetailFact = ({classes, icon, label, value}) => (
	<div className={classes.ticketDetailFact}>
		<span className={classes.ticketDetailFactIcon}>{icon}</span>
		<span className={classes.ticketDetailFactLabel}>{label}</span>
		<strong className={classes.ticketDetailFactValue}>{value || "Non disponibile"}</strong>
	</div>
);

const TicketDetailSkeleton = ({classes}) => (
	<div className={classes.ticketDetailShell}>
		<Skeleton height={150} variant="rounded"/>
		<div className={classes.ticketDetailTopGrid}>
			<Skeleton height={210} variant="rounded"/>
			<Skeleton height={210} variant="rounded"/>
		</div>
		<Skeleton height={92} variant="rounded"/>
		<Skeleton height={360} variant="rounded"/>
	</div>
);

const CustomerSummary = ({anagrafica, classes, isAnonymous, loading, ticket}) => {
	const customer = getCustomerData(anagrafica, ticket);
	const fullName = isAnonymous
		? "Utente anonimo"
		: `${customer.nome || "Nome non disponibile"} ${customer.cognome || ""}`.trim();

	return (
		<Paper className={clsx(classes.ticketDetailPanel, classes.ticketDetailCustomerPanel)} component="section" elevation={0}>
			<div className={classes.ticketDetailPanelHeader}>
				<Avatar className={classes.ticketDetailPanelIcon}>
					{isAnonymous ? <PersonOffTwoToneIcon/> : <PersonTwoToneIcon/>}
				</Avatar>
				<div>
					<Typography component="h2" className={classes.ticketDetailPanelTitle}>
						Anagrafica cliente
					</Typography>
					<Typography className={classes.ticketDetailPanelSubtitle}>
						{loading ? "Caricamento dati cliente..." : fullName}
					</Typography>
				</div>
			</div>
			<div className={classes.ticketDetailMiniFacts}>
				{loading ? (
					<React.Fragment>
						<Skeleton height={42} variant="rounded"/>
						<Skeleton height={42} variant="rounded"/>
						<Skeleton height={42} variant="rounded"/>
						<Skeleton height={42} variant="rounded"/>
					</React.Fragment>
				) : (
					<React.Fragment>
						<TicketDetailFact classes={classes} icon={<PersonTwoToneIcon/>} label="Nome" value={customer.nome || (isAnonymous ? "Anonimo" : "")}/>
						<TicketDetailFact classes={classes} icon={<PersonTwoToneIcon/>} label="Cognome" value={customer.cognome || (isAnonymous ? "Utente" : "")}/>
						<TicketDetailFact classes={classes} icon={<PhoneIphoneTwoToneIcon/>} label="Cellulare" value={customer.cell}/>
						<TicketDetailFact classes={classes} icon={<EmailTwoToneIcon/>} label="Mail" value={customer.email}/>
					</React.Fragment>
				)}
			</div>
		</Paper>
	);
};

const RequestSummary = ({classes, readableRequest, ticket}) => (
	<Paper className={clsx(classes.ticketDetailPanel, classes.ticketDetailRequestPanel)} component="section" elevation={0}>
		<div className={classes.ticketDetailPanelHeader}>
			<Avatar className={classes.ticketDetailPanelIcon}>
				<AssignmentTwoToneIcon/>
			</Avatar>
			<div>
				<Typography className={classes.ticketDetailKicker}>
					Ticket #{ticket.id}
				</Typography>
				<Typography component="h1" className={classes.ticketDetailPanelTitle}>
					Richiesta
				</Typography>
				<Typography className={classes.ticketDetailPanelSubtitle}>
					Messaggio originale ricevuto dal richiedente.
				</Typography>
			</div>
		</div>
		<Typography className={classes.ticketDetailRequestText}>
			{readableRequest || "Nessun messaggio testuale disponibile per questo ticket."}
		</Typography>
		{ticket?.dynamicFormStructure && (
			<div className={classes.ticketDetailRequestFooter}>
				<DynamicFormLauncher classes={classes} ticket={ticket}/>
			</div>
		)}
	</Paper>
);

const DynamicFormLauncher = ({classes, ticket}) => {
	const [open, setOpen] = useState(false);

	return (
		<React.Fragment>
			<Button
				className={clsx(classes.ticketDetailActionButton, classes.ticketDetailActionPrimary)}
				disableElevation
				endIcon={<ArticleTwoToneIcon/>}
				onClick={() => setOpen(true)}
				size="small"
				variant="contained"
			>
				<b>Dati aggiuntivi pratica</b>
			</Button>
			{open && (
				<SimpleDialog
					title="Dati aggiuntivi pratica"
					maxWidth="lg"
					open={open}
					handleClose={() => setOpen(false)}
					paperClassName={classes.ticketPracticeDialog}
					contentClassName={classes.ticketPracticeDialogContent}
					showCloseButton
					content={(
						<DynamicForm
							formName="Dati aggiuntivi pratica"
							data={ticket?.dynamicFormData}
							editable={true}
							isLoading={false}
							clienteCensito={false}
							fields={ticket?.dynamicFormStructure}
						/>
					)}
				/>
				)}
			</React.Fragment>
	);
};

const TicketDetailPage = () => {
	const ticketClasses = TicketStyle();
	const ticketContext = useContext(TicketContext);
	const anagraficaContext = useContext(AnagraficaContext);
	const appContext = useContext(AppContext);
	const {id} = useParams();
	const history = useHistory();
	const [ticketSelected, setTicketSelected] = useState();
	const [checkTicket, setCheckTicket] = useState(false);
	const [automationState, automationDispatch] = useReducer(AutomationReducer, AUTOMATION_INITIAL_STATE, initAutomationState);

	const loadingTicket = ticketContext.state.loadingSearchTicket;
	const ticketError = ticketContext.state.errorSearchTicket;
	const isAnonymous = ticketSelected?.soggetto?.codF === "UTENTE_ANONIMO";
	const readableRequest = cleanTicketText(ticketSelected?.dettaglioRichiesta);
	const assignee = getAssigneeLabel(ticketSelected, automationState);

	useEffect(() => {
		onClickDetails(id, "ticket", "modify", automationDispatch);
	}, [id]);

	useEffect(() => {
		if (!automationState.loadingOnClick && automationState.errorOnClick === "") {
			if (!(automationState.operatorOnClick.replaceAll("ERR_", "").includes(getAccountInfo().alias))) {
				history.push("/gestione/tickets");
			}
		}
	}, [automationState.loadingOnClick, automationState.errorOnClick, automationState.operatorOnClick,
		anagraficaContext.dispatch, history, appContext.dispatch]);

	useEffect(() => {
		getTicketHistory({id: id}, ticketContext.dispatch);
	}, [ticketContext.dispatch, id]);

	useEffect(() => {
		searchTicket({id: id}, ticketContext.dispatch);
		setCheckTicket(true);
	}, [id, ticketContext.dispatch]);

	useEffect(() => {
		if (checkTicket && !ticketContext.state.loadingSearchTicket && ticketContext.state.errorSearchTicket === "") {
			const selectedTicket = ticketContext.state.searchedTickets[0];
			setTicketSelected(selectedTicket);

			if (selectedTicket?.idSoggetto) {
				getAnagraficaById(selectedTicket.idSoggetto, anagraficaContext.dispatch);
			}

			if (selectedTicket?.pazientiAssociati) {
				getPazientiAssociati(selectedTicket, anagraficaContext.dispatch);
			}
		}
	}, [ticketContext.state.loadingSearchTicket, ticketContext.state.errorSearchTicket,
		ticketContext.state.searchedTickets, anagraficaContext.dispatch, checkTicket]);

	return (
		<React.Fragment>
			<Backdrop open={automationState.loadingOnClick} className={ticketClasses.backdrop}>
				<CircularProgress/>
			</Backdrop>
			<AppPage
				title={`Ticket ${id}`}
				appBarProps={{goHome: false, back: true}}
				mainClassName={ticketClasses.pageRoot}
				containerClassName={clsx(ticketClasses.pageContainer, ticketClasses.ticketDetailContainer)}
			>
				{loadingTicket && <TicketDetailSkeleton classes={ticketClasses}/>}

				{!loadingTicket && ticketError !== "" && (
					<Alert severity="error" className={ticketClasses.ticketAlert}>
						<AlertTitle>Errore dettaglio ticket</AlertTitle>
						Non e stato possibile recuperare i dati del ticket.
					</Alert>
				)}

				{!loadingTicket && ticketError === "" && ticketSelected && (
					<div className={ticketClasses.ticketDetailShell}>
						<TicketSummaryManager
							assignee={assignee}
							classes={ticketClasses}
							ticket={ticketSelected}
						/>

						<div className={ticketClasses.ticketDetailTopGrid}>
							<RequestSummary
								classes={ticketClasses}
								readableRequest={readableRequest}
								ticket={ticketSelected}
							/>
							<CustomerSummary
								anagrafica={anagraficaContext.state.anagrafica}
								classes={ticketClasses}
								isAnonymous={isAnonymous}
								loading={anagraficaContext.state.loadingGetAnagrafica && !isAnonymous}
								ticket={ticketSelected}
							/>
						</div>

						<div className={ticketClasses.ticketDetailHistoryRow}>
							<StoricoCard useStringify={ticketSelected?.campoAgg1 === "#RIENTRO"}>
								<TicketHistoryComposer ticketSelected={ticketSelected}/>
							</StoricoCard>
						</div>
					</div>
				)}
			</AppPage>
		</React.Fragment>
	);
};

export default React.memo(TicketDetailPage);
